import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/superbuy';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="hero" className="relative w-full h-screen min-h-[700px] flex items-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Artistic%20flat%20lay%20composition%20of%20diverse%20fashion%20accessories%20including%20sunglasses%20leather%20wallet%20silk%20scarf%20and%20minimalist%20watch%20arranged%20on%20textured%20cream%20linen%20surface%20with%20soft%20morning%20light%20casting%20gentle%20shadows%2C%20dried%20eucalyptus%20sprigs%20and%20ceramic%20vase%20accents%2C%20warm%20neutral%20tones%20with%20subtle%20sage%20green%20elements%2C%20editorial%20lifestyle%20product%20photography%20with%20sophisticated%20organic%20arrangement%20and%20airy%20peaceful%20atmosphere&width=1920&height=1080&seq=hero-main-bg-v3&orientation=landscape"
        alt="Superbuy Spreadsheet product finds and shopping resources hero background"
        title="Superbuy Spreadsheet - Product Finds & Shopping Resources"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/88 via-background-50/60 to-transparent"></div>

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <p className="text-xs tracking-[0.2em] uppercase text-foreground-600 mb-5 font-medium">
            The Ultimate Resource Hub
          </p>

          <h1 className="font-heading text-5xl md:text-7xl lg:text-[80px] font-bold text-foreground-950 leading-[1.08] mb-6">
            Superbuy Spreadsheet
            <br />
            <span className="text-primary-500">Smart Shopping Hub</span>
          </h1>

          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-10 max-w-lg">
            Discover organized product finds, shopping guides, and expert resources — everything you need for smarter international shopping in one place.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 px-7 py-3.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 flex items-center gap-2"
            >
              Superbuy Sheet
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="border-2 border-foreground-800 hover:bg-foreground-800 hover:text-background-50 text-foreground-800 px-7 py-3.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300"
            >
              Visit Main Website
            </a>
          </div>

          <div ref={dropdownRef} className="relative w-full max-w-sm mb-8">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full bg-white border border-primary-400/50 rounded-xl px-5 py-3.5 flex items-center justify-between cursor-pointer transition-all duration-200 hover:border-primary-500"
            >
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-grid-line text-primary-500 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-700">
                  {selectedCategory || 'Browse Product Categories'}
                </span>
              </div>
              <div className="w-5 h-5 flex items-center justify-center">
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-foreground-500 text-sm"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-foreground-500 text-sm"></i>
                )}
              </div>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-background-200 rounded-xl overflow-hidden z-30 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleSelect(cat)}
                    className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-primary-50 cursor-pointer transition-colors text-sm text-foreground-700"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-primary-400 text-sm`}></i>
                    </div>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 bg-primary-100/80 text-primary-700 text-xs font-medium px-3 py-1.5 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-500"></div>
              Updated 2026
            </span>
            <span className="inline-flex items-center gap-1.5 bg-accent-100/80 text-accent-700 text-xs font-medium px-3 py-1.5 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-accent-500"></div>
              Beginner Friendly
            </span>
            <span className="inline-flex items-center gap-1.5 bg-secondary-100/80 text-secondary-700 text-xs font-medium px-3 py-1.5 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-secondary-500"></div>
              Safe Links
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}